import moment from "moment";

/**
 * Mirrors backend journey_schedule_helpers.buildJourneySchedule so admin/coach
 * People % and staff check-in dates match the member portal.
 */

const MAX_SCHEDULE_DAYS = 3 * 366;
const WEEKDAY_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};
const ORDINAL_INDEX = { first: 1, second: 2, third: 3, fourth: 4 };

const toDay = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.clone().startOf("day") : null;
};

// A late check-in counts on the missed check-in date it was sent for.
const rowDay = (row) =>
  toDay(row?.scheduledFor || row?.submittedAt || row?.createdAt);

const isLeadCapture = (tracker) =>
  [tracker?.category, tracker?.type].some(
    (value) => String(value || "").toLowerCase() === "lead_capture"
  );

const normalizeCheckInMode = (value) => {
  if (value === "daily") return "daily";
  if (value === "monthly") return "monthly";
  if (value === "custom" || value === "every_n_days") return "custom";
  return "weekly";
};

const journeyLengthDays = (setup = {}) => {
  const duration = setup.durationWeeks;
  if (duration === "ongoing" || duration == null || duration === "") {
    return null;
  }
  if (duration === "custom") {
    return Math.max(1, Number(setup.customDurationDays) || 21);
  }
  return Math.max(1, Number(duration) || 12) * 7;
};

const startsFromPublishDate = (value) =>
  value === "from_publish_date" || value === "coach_defined";

const isMonthlyCheckInDay = (setup, date) => {
  if (setup.checkInMonthlyStyle === "on_the_week_and_day") {
    const weekdayKey = String(setup.checkInMonthWeekday || "monday").toLowerCase();
    const weekday = WEEKDAY_INDEX[weekdayKey] ?? 1;
    if (date.day() !== weekday) return false;
    const ordinal = String(setup.checkInMonthWeekOrdinal || "first").toLowerCase();
    if (ordinal === "last") {
      return date.clone().add(7, "days").month() !== date.month();
    }
    return Math.ceil(date.date() / 7) === (ORDINAL_INDEX[ordinal] || 1);
  }
  const wanted = Math.min(
    Math.max(1, Number(setup.checkInMonthDay) || 1),
    date.daysInMonth()
  );
  return date.date() === wanted;
};

const isCadenceDay = (setup, startDay, date) => {
  const mode = normalizeCheckInMode(setup.checkInMode);
  if (mode === "daily") return true;
  if (mode === "custom") {
    const every = Math.max(1, Number(setup.checkInEveryDays) || 7);
    return date.diff(startDay, "days") % every === 0;
  }
  if (mode === "monthly") return isMonthlyCheckInDay(setup, date);
  const weekdays = Array.isArray(setup.checkInWeekdays)
    ? setup.checkInWeekdays.map(Number)
    : [];
  return weekdays.includes(date.day());
};

export const getRecurringForms = (tracker) => {
  if (!tracker?.createSetup || isLeadCapture(tracker)) return null;
  const forms = (Array.isArray(tracker.forms) ? tracker.forms : []).filter(
    Boolean
  );
  const startForm =
    forms.find((f) => f.role === "first_submission") || forms[0];
  if (!startForm) return null;
  if (forms.length === 1) {
    return tracker.questionPattern === "same_questions_repeating"
      ? { startForm, checkInForm: startForm }
      : null;
  }
  if (forms.length === 2) {
    return { startForm, checkInForm: forms.find((f) => f !== startForm) };
  }
  return null;
};

const splitRows = (recurring, rows = []) => {
  const sorted = [...rows].sort(
    (a, b) =>
      new Date(a.submittedAt || a.createdAt || 0) -
      new Date(b.submittedAt || b.createdAt || 0)
  );
  if (recurring.checkInForm === recurring.startForm) {
    return { startRow: sorted[0] || null, checkInRows: sorted.slice(1) };
  }
  const checkInId = String(recurring.checkInForm.id);
  return {
    startRow: sorted.find((row) => String(row.formId) !== checkInId) || null,
    checkInRows: sorted.filter((row) => String(row.formId) === checkInId),
  };
};

const checkInDates = (setup, startDay, endDay, today) => {
  const dates = [];
  const cursor = startDay.clone().add(1, "day");
  for (let i = 0; i < MAX_SCHEDULE_DAYS; i += 1) {
    if (endDay && cursor.isAfter(endDay, "day")) break;
    if (isCadenceDay(setup, startDay, cursor)) {
      dates.push(cursor.clone());
      if (!endDay && cursor.isAfter(today, "day")) break;
    }
    cursor.add(1, "day");
  }
  return dates;
};

/**
 * → { steps, doneCount, total, progressPercent } or null when not a
 * recurring check-in journey.
 */
export const buildJourneySchedule = (tracker, rows = [], today = moment()) => {
  const recurring = getRecurringForms(tracker);
  if (!recurring) return null;
  const setup = tracker.createSetup || {};
  const todayDay = toDay(today) || moment().startOf("day");
  const { startRow, checkInRows } = splitRows(recurring, rows);

  let joinDay = null;
  rows.forEach((row) => {
    const day = toDay(row.createdAt || row.submittedAt);
    if (day && (!joinDay || day.isBefore(joinDay))) joinDay = day;
  });
  if (!joinDay) joinDay = todayDay.clone();

  const publishDay = toDay(tracker.publishDate || setup.publishDate);
  const startDay =
    startsFromPublishDate(tracker.journeyStartsFrom || setup.journeyStartsFrom) &&
    publishDay
      ? publishDay
      : joinDay;
  const length = journeyLengthDays(setup);
  const endDay = length ? startDay.clone().add(length, "days") : null;

  const dates = checkInDates(setup, startDay, endDay, todayDay);
  const occurrences = dates
    .map((opens, i) => ({ opens, closes: dates[i + 1] || null }))
    .filter(({ closes }) => !closes || closes.isAfter(joinDay, "day"));

  checkInRows.forEach((row) => {
    const day = rowDay(row);
    if (!day || !occurrences.length) return;
    const match =
      occurrences.find(
        ({ opens, closes }) =>
          !day.isBefore(opens, "day") && (!closes || day.isBefore(closes, "day"))
      ) || (day.isBefore(occurrences[0].opens, "day") ? occurrences[0] : null);
    if (match) match.row = row;
  });

  const separateStart = recurring.checkInForm !== recurring.startForm;
  const steps = [
    {
      id: `start_${recurring.startForm.id}`,
      formId: String(recurring.startForm.id),
      kind: "start",
      name: separateStart ? recurring.startForm.name || "Start form" : "Start",
      state: startRow ? "done" : "current",
      opensAt: startDay.toISOString(),
      closesAt: null,
      submissionId: startRow ? String(startRow.id || startRow._id || "") : null,
      selectable: !startRow,
    },
  ];

  let hasCurrent = false;
  const checkInSteps = occurrences.map(({ opens, closes, row }, i) => {
    let state;
    if (row) state = "done";
    else if (!startRow || opens.isAfter(todayDay, "day")) state = "upcoming";
    else if (closes && !closes.isAfter(todayDay, "day")) state = "missed";
    else {
      state = "current";
      hasCurrent = true;
    }
    return {
      id: `checkin_${i + 1}_${opens.format("YYYYMMDD")}`,
      formId: String(recurring.checkInForm.id),
      kind: "check_in",
      name: opens.format("D MMM YYYY"),
      state,
      opensAt: opens.toISOString(),
      closesAt: closes ? closes.toISOString() : null,
      submissionId: row ? String(row.id || row._id || "") : null,
      selectable: state === "current" || state === "missed",
    };
  });
  if (startRow && !hasCurrent) {
    const next = checkInSteps.find((step) => step.state === "upcoming");
    if (next) {
      next.state = "locked";
      next.selectable = false;
    }
  }
  steps.push(...checkInSteps);

  const doneCount = steps.filter((step) => step.state === "done").length;
  return {
    startAt: startDay.toISOString(),
    endAt: endDay ? endDay.toISOString() : null,
    steps,
    doneCount,
    total: steps.length,
    progressPercent: Math.round((doneCount / steps.length) * 100),
    checkInFormId: String(recurring.checkInForm.id),
    startFormId: String(recurring.startForm.id),
  };
};

export const formatScheduleStepLabel = (step) => {
  if (!step) return "";
  if (step.kind === "start") return step.name || "Start";
  return step.name || moment(step.opensAt).format("D MMM YYYY");
};

const POINT_UNITS = new Set(["days", "weeks", "months"]);

const schedulePointDate = (startDay, point) => {
  if (point.unit === "days") {
    return startDay.clone().add(point.amount - 1, "days");
  }
  if (point.unit === "weeks") return startDay.clone().add(point.amount * 7, "days");
  return startDay.clone().add(point.amount, "months");
};

/**
 * Mirrors backend schedule_point_questions.getCheckInSchedulePoint: the saved
 * Journey Submission scheduling point whose window holds `at` (same window
 * rule as the automations) with its extra / excluded questions, or null.
 */
export const findCheckInSchedulePoint = (tracker, rows = [], at = moment()) => {
  const saved = (
    Array.isArray(tracker?.reviewPointSettings) ? tracker.reviewPointSettings : []
  ).filter(
    (p) =>
      p &&
      Number(p.amount) > 0 &&
      POINT_UNITS.has(String(p.unit || "").toLowerCase())
  );
  if (!saved.length || !rows.length) return null;

  let joinedDay = null;
  rows.forEach((row) => {
    const day = toDay(row.createdAt || row.submittedAt);
    if (day && (!joinedDay || day.isBefore(joinedDay))) joinedDay = day;
  });
  const setup = tracker.createSetup || {};
  const startsFrom = tracker.journeyStartsFrom || setup.journeyStartsFrom;
  const publishDay = toDay(tracker.publishDate || setup.publishDate);
  const startDay =
    (startsFrom === "from_publish_date" || startsFrom === "coach_defined") &&
    publishDay
      ? publishDay
      : joinedDay;
  const day = toDay(at);
  if (!startDay || !day) return null;

  const dated = saved.map((raw) => {
    const point = {
      amount: Number(raw.amount),
      unit: String(raw.unit).toLowerCase(),
    };
    return { raw, due: schedulePointDate(startDay, point) };
  });
  const hit = dated.find(({ due }) => {
    const previous = dated
      .map((other) => other.due)
      .filter((date) => date.isBefore(due, "day"))
      .sort((a, b) => a.valueOf() - b.valueOf())
      .pop();
    const from = previous
      ? previous.clone().add(1, "day")
      : due.isSame(startDay, "day")
        ? startDay.clone()
        : startDay.clone().add(1, "day");
    return !day.isBefore(from, "day") && !day.isAfter(due, "day");
  });
  if (!hit) return null;

  const extraQuestions = (hit.raw.extraQuestions || []).filter(
    (q) => q && q.id && String(q.label || "").trim()
  );
  const excludedSharedQuestionIds = (hit.raw.excludedSharedQuestionIds || []).map(
    String
  );
  if (!extraQuestions.length && !excludedSharedQuestionIds.length) return null;
  return {
    key: String(hit.raw.key || ""),
    label: hit.raw.label || "",
    extraQuestions,
    excludedSharedQuestionIds,
  };
};
